/**
 * Attribute validation for the inventory catalog (BON-161) — frontend mirror of
 * backend/src/services/inventory-catalog.validation.ts. Kept in sync by hand so
 * the in-app forms validate identically to the API. If you change one, change
 * both.
 */
export type AttributeDataType = 'string' | 'number' | 'enum' | 'duration_days' | 'boolean';

export interface AttributeDefinitionLike {
  name: string;
  dataType: AttributeDataType | string;
  allowedValues: unknown;
  isRequired: boolean;
  isActive: boolean;
}

export type AttributeValues = Record<string, unknown>;

export type ValidateResult =
  | { ok: true; value: AttributeValues }
  | { ok: false; errors: Record<string, string> };

function isBlank(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

type Coerced = { value: unknown } | { error: string };

function coerceNumber(name: string, raw: unknown): Coerced {
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? { value: n } : { error: `${name} must be a number` };
}

function coerceBoolean(name: string, raw: unknown): Coerced {
  if (typeof raw === 'boolean') return { value: raw };
  const s = String(raw).trim().toLowerCase();
  if (s === 'true') return { value: true };
  if (s === 'false') return { value: false };
  return { error: `${name} must be true or false` };
}

function coerceEnum(def: AttributeDefinitionLike, raw: unknown): Coerced {
  const allowed = Array.isArray(def.allowedValues)
    ? (def.allowedValues as unknown[]).map(String)
    : [];
  const s = String(raw);
  return allowed.includes(s)
    ? { value: s }
    : { error: `${def.name} must be one of: ${allowed.join(', ') || '(no values defined)'}` };
}

function coerceValue(def: AttributeDefinitionLike, raw: unknown): Coerced {
  switch (def.dataType) {
    case 'number':
    case 'duration_days':
      return coerceNumber(def.name, raw);
    case 'boolean':
      return coerceBoolean(def.name, raw);
    case 'enum':
      return coerceEnum(def, raw);
    case 'string':
      return { value: String(raw) };
    default:
      return { value: raw };
  }
}

/** Validate (and normalize) an attributes blob against a category's definitions. */
export function validateAttributes(
  definitions: AttributeDefinitionLike[],
  attributes: AttributeValues,
): ValidateResult {
  const errors: Record<string, string> = {};
  const out: AttributeValues = { ...attributes };

  for (const def of definitions) {
    if (!def.isActive) continue;
    const raw = attributes[def.name];

    if (isBlank(raw)) {
      if (def.isRequired) errors[def.name] = `${def.name} is required`;
      if (def.name in out && isBlank(out[def.name])) delete out[def.name];
      continue;
    }

    const coerced = coerceValue(def, raw);
    if ('error' in coerced) errors[def.name] = coerced.error;
    else out[def.name] = coerced.value;
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, value: out };
}
